/** @jsxImportSource @emotion/react */
import React, { FC, useEffect } from 'react';
import { FormikErrors } from 'formik';
import { Selectable, TextFields } from '@zip/business-components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  POSPlatformProps,
  StepComponentProps,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { logEvent, Validators } from 'utils';
import { StepIndex } from '../signUpConfig';
import * as styles from './SignUpForm.styles';
import {
  SignUpRadioStepConfig,
  SignUpRadioStepForm,
  useSignUpRadioStep,
} from './useSignUpRadioStep.module';
import { PageAction } from 'enums';

const OTHER_OPTION = 'Other';
const OTHER_PLATFORM_MAX_LENGTH = 100;

const validateOtherPlatform = (
  values: POSPlatformProps
): FormikErrors<POSPlatformProps> => {
  const errors: FormikErrors<POSPlatformProps> = {};

  if (values.platform !== OTHER_OPTION) {
    return errors;
  }

  if (!values.otherPlatform?.trim()) {
    errors.otherPlatform = 'Please specify your POS platform';
  } else if (values.otherPlatform.length > OTHER_PLATFORM_MAX_LENGTH) {
    errors.otherPlatform = `Must be ${OTHER_PLATFORM_MAX_LENGTH} characters or less`;
  }

  return errors;
};

const config: SignUpRadioStepConfig<POSPlatformProps> = {
  stepIndex: StepIndex.POSPlatform,
  selectionField: 'platform',
  requiredSelectionMessage: 'Please select an option',
  initialValues: {
    platform: '',
    otherPlatform: '',
  },
  validate: validateOtherPlatform,
};

export const POSPlatformStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  serverErrors,
}) => {
  const { formOptions, merchantApplicationId } =
    useMerchantApplicationContext();
  const options: Selectable[] = (formOptions.POSPlatform ?? []).map(
    (answer) => ({ label: answer, value: answer })
  );
  const step = useSignUpRadioStep(config, options, {
    provideSubmit,
    serverErrors,
  });
  const isOtherSelected = step.formik.values.platform === OTHER_OPTION;

  useEffect(() => {
    logEvent(PageAction.merchantOnboardingPVPOSPlatform, {
      merchantApplicationId,
    });
  }, []);

  useEffect(() => {
    if (!isOtherSelected && step.formik.values.otherPlatform) {
      step.formik.setFieldValue('otherPlatform', '');
    }
  }, [isOtherSelected, step.formik]);

  return (
    <>
      <SignUpRadioStepForm {...step} />
      {isOtherSelected && (
        <TextFields.Outlined
          type="text"
          name="otherPlatform"
          label="Please specify your POS platform"
          css={[styles.textfield, styles.textfieldNoMargin]}
          fullWidth
          value={step.formik.values.otherPlatform ?? ''}
          onChange={step.formik.handleChange}
          onBlur={step.formik.handleBlur}
          inputProps={{ maxLength: OTHER_PLATFORM_MAX_LENGTH }}
          error={
            step.submitAttempted && Boolean(step.formik.errors.otherPlatform)
          }
          helperText={Validators.renderFieldError(
            step.formik.touched.otherPlatform,
            step.formik.errors.otherPlatform,
            step.submitAttempted
          )}
        />
      )}
    </>
  );
};

export default POSPlatformStepModule;
